"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { fetcher } from "@/lib/http/fetcher";
import {
  Button,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Grid,
  Input,
  Textarea,
} from "@/ui";

// define form schema
const formSchema = z.object({
  name: z.string().min(1).max(50),
  phone: z.string().min(1).max(50).optional(),
  email: z.string().email().min(1).max(50),
  message: z.string().min(1),
});

type FormValues = z.infer<typeof formSchema>;

export const ContactForm = () => {
  const generalT = useTranslations("general");
  const validationT = useTranslations("validation");
  const contactT = useTranslations("contact");
  const toastT = useTranslations("toast");
  const locale = useLocale();
  const [isLoading, setIsLoading] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = async (values: FormValues) => {
    try {
      setIsLoading(true);
      await fetcher(`/${locale}/api/contact`, {
        method: "POST",
        body: JSON.stringify(values),
      });
      form.reset();
      toast.success(toastT("success.contact_message"));
    } catch (err) {
      console.error("Error submitting contact form:", err);
      toast.error(toastT("error.message"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="px-6 pb-12 sm:pb-24 lg:px-8 lg:py-24"
      >
        <Grid className="mx-auto max-w-xl grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:mr-0 lg:max-w-lg">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-1">
                <FormControl>
                  <Input
                    placeholder={contactT("your_name")}
                    {...field}
                    className="input-luxury"
                  />
                </FormControl>
                <FormMessage>
                  {errors.name && validationT("required", { field: "name" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem className="col-span-1">
                <FormControl>
                  <Input
                    placeholder={contactT("your_phone")}
                    {...field}
                    className="input-luxury"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormControl>
                  <Input
                    placeholder={contactT("your_email")}
                    {...field}
                    className="input-luxury"
                  />
                </FormControl>
                <FormMessage>{errors.email && validationT("email")}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="message"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormControl>
                  <Textarea
                    placeholder={contactT("your_message")}
                    {...field}
                    className="input-luxury h-32 w-full resize-none"
                  />
                </FormControl>
                <FormMessage>
                  {errors.message && validationT("required", { field: "message" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="btn-lift col-span-2"
            disabled={isLoading || !form.formState.isValid}
          >
            <span className={cn(isLoading ? "loading loading-spinner" : "hidden")} />
            {generalT("submit")}
          </Button>
        </Grid>
      </form>
    </Form>
  );
};
